import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { AppContext } from '../AppContext';
import { GetTimesheetSummaryEvent, GetTimesheetSummaryResult, TimesheetDayTotal } from 'bloggers-backend/lib/timesheetReport/domain';
import { GetTimesheetEvent, GetTimesheetResult, Timesheet } from 'bloggers-backend/lib/timesheetReport/domain';
import { UserLinkInput } from '../components/UserLinkInput';

interface User {
  id: string;
  name: string;
}

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours} h ${mins} min` : `${mins} min`;
}

export function TimesheetSummaryPage() {
  const { notificationsService, client } = React.useContext(AppContext);

  const [dateRange, setDateRange] = React.useState<DateRange<Dayjs>>([
    dayjs().subtract(1, 'month'), dayjs().add(1, 'day')
  ]);

  const [summary, setSummary] = React.useState<TimesheetDayTotal[]>([]);
  const [user, setUser] = React.useState<User>();

  const onClick = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    const result = await client<GetTimesheetSummaryEvent, GetTimesheetSummaryResult>({
      event: 'getTimesheetSummary',
      data: {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        userName: user?.name || "",
      }
    });

    if (result.success) {
      setSummary(result.value);
    } else {
      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`
      });
    }
  };

  const [openedDate, setOpenedDate] = React.useState<string | null>(null);
  const [details, setDetails] = React.useState<Record<string, Timesheet>>({});
  const [loadingDate, setLoadingDate] = React.useState<string | null>(null);

  const loadDetails = async (date: string) => {
    if (details[date]) {
      setOpenedDate(openedDate === date ? null : date);
      return;
    }

    setLoadingDate(date);

    const result = await client<GetTimesheetEvent, GetTimesheetResult>({
      event: 'getTimesheet',
      data: {
        startDate: date,
        endDate: dayjs(date).add(1, 'day').format('YYYY-MM-DD'),
        userName: user?.name || "",
      }
    });

    setLoadingDate(null);

    if (result.success) {
      setDetails(prev => ({ ...prev, [date]: result.value }));
      setOpenedDate(date);
    } else {
      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`
      });
    }
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div style={{ marginBottom: '20px' }}>
          <DateRangePicker 
            value={dateRange}
            onChange={(newValue) => setDateRange(newValue)}
            format="DD/MM/YYYY"
            localeText={{ start: 'start date', end: 'end date' }} 
          />
        </div>
      </LocalizationProvider>

      <UserLinkInput
        id="manager"
        name="manager"
        label="manager"
        variant="outlined"
        required
        onChoose={(manager) => setUser(manager || undefined)}
        value={user || null}
      />

      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <Button onClick={onClick}>Get Summary</Button>
      </div>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 300 }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold', width: '30%' }}>Date</TableCell>
              <TableCell style={{ fontWeight: 'bold', width: '30%' }}>Time Worked</TableCell>
              <TableCell style={{ fontWeight: 'bold', width: '40%' }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
  {summary.map(row => {
    const isOpen = openedDate === row.date;
    const dayDetails = details[row.date];

    return (
      <React.Fragment key={row.date}>
        <TableRow>
          <TableCell>{dayjs(row.date).format('D MMMM')}</TableCell>
          <TableCell>{formatMinutes(row.totalTime)}</TableCell>
          <TableCell>
            <Button
              size="small"
              onClick={() => loadDetails(row.date)}
              disabled={loadingDate === row.date}
            >
              {isOpen ? 'Hide' : 'Detail'}
            </Button>
          </TableCell>
        </TableRow>

        {isOpen && (
          <TableRow>
            <TableCell colSpan={3}>
              {!dayDetails ? (
                'Loading...'
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayDetails.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{dayjs(item.time).format('HH:mm')}</TableCell>
                        <TableCell>{item.entityName}</TableCell>
                        <TableCell>
                          <pre style={{ margin: 0 }}>
                            {JSON.stringify(item.changes, null, 2)}
                          </pre>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  })}
</TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
